import React from 'react';

import { StarDisplay } from 'components/StarDisplay';
import { Colors } from 'constants/ui';
import { formatDateShort } from 'utils/formatters';

import { formatDaysAfter, timeframeDisplay } from '../helpers';
import * as styled from '../styles/reviews';

type Props = {
  review: Kudable.Review;
  accentColor: string;
  onOpenLightbox: (asset: Profile.LightboxAsset) => void;
  onAskInquiry: (review: Kudable.Review) => void;
  hideStars?: boolean;
};

export const ReviewCardBody = ({
  review,
  accentColor,
  onOpenLightbox,
  onAskInquiry,
  hideStars = false,
}: Props) => (
  <>
    {review.service_name && (
      <styled.ServiceName>{review.service_name}</styled.ServiceName>
    )}
    <styled.TopRow>
      <styled.Name>
        {review.reviewer_name || 'Anonymous'}
        {review.reviewer_organisation ? (
          <styled.Organisation>· {review.reviewer_organisation}</styled.Organisation>
        ) : null}
        {review.badge === 'job_confirmed' && (
          <styled.Badge
            $bg={accentColor}
            $color={Colors.white}
            title="Job Confirmed — provider entered the client's completion code"
          >
            🛡 Job Confirmed
          </styled.Badge>
        )}
        {review.badge === 'booked_verified' && (
          <styled.Badge
            $bg={Colors.successBgSoft}
            $color={Colors.successDarkest}
            title="Booked & verified — booked through Kudable, awaiting job completion"
          >
            ✓ Booked & verified
          </styled.Badge>
        )}
        {review.was_edited && (
          <styled.EditedChip
            title={
              review.updated_at
                ? `Last updated ${formatDateShort(review.updated_at)}`
                : 'Edited'
            }
          >
            ✎ Edited
          </styled.EditedChip>
        )}
      </styled.Name>
      <styled.Date>
        {formatDateShort(review.inserted_at)}
        {review.days_after_job !== null && (
          <> · {formatDaysAfter(review.days_after_job)}</>
        )}
      </styled.Date>
    </styled.TopRow>
    {!hideStars &&
      (review.original_overall_rating !== null &&
      review.original_overall_rating !== review.overall_rating ? (
        <styled.RatingDelta>
          <styled.RatingLabel>Originally</styled.RatingLabel>
          <styled.RatingStars>
            {'★'.repeat(review.original_overall_rating)}
            {'☆'.repeat(5 - review.original_overall_rating)}
          </styled.RatingStars>
          <styled.RatingArrow>→</styled.RatingArrow>
          <styled.RatingLabel>Now</styled.RatingLabel>
          <styled.RatingStars>
            {'★'.repeat(review.overall_rating)}
            {'☆'.repeat(5 - review.overall_rating)}
          </styled.RatingStars>
          <styled.UpdatedChip>updated</styled.UpdatedChip>
        </styled.RatingDelta>
      ) : (
        <StarDisplay value={review.overall_rating} size={14} />
      ))}
    {review.original_comment && review.original_comment !== review.comment && (
      <styled.OriginalComment>
        <styled.CommentLabel>Originally:</styled.CommentLabel> "
        {review.original_comment}"
      </styled.OriginalComment>
    )}
    {review.achievement ? (
      <styled.Achievement>
        🎯 {review.achievement}
        {review.timeframe ? (
          <styled.AchievementTimeframe>
            · {timeframeDisplay(review.timeframe)}
          </styled.AchievementTimeframe>
        ) : null}
      </styled.Achievement>
    ) : null}
    {review.comment && (
      <styled.Comment>
        {review.original_comment && review.original_comment !== review.comment && (
          <styled.CommentLabel>Now: </styled.CommentLabel>
        )}
        "{review.comment}"
      </styled.Comment>
    )}
    {review.improvement_suggestion && (
      <styled.ImprovementBox>
        <styled.ImprovementLabel>💡 Could improve</styled.ImprovementLabel>
        <styled.ImprovementBody>
          "{review.improvement_suggestion}"
        </styled.ImprovementBody>
      </styled.ImprovementBox>
    )}
    {review.assets && review.assets.length > 0 && (
      <styled.MediaRow>
        {review.assets.map((asset) =>
          asset.kind === 'audio' ? (
            <styled.AudioCard key={asset.id}>
              <styled.AudioIcon>🎤</styled.AudioIcon>
              <styled.AudioPlayer controls preload="metadata" src={asset.url} />
            </styled.AudioCard>
          ) : (
            <styled.Thumb
              key={asset.id}
              onClick={() =>
                onOpenLightbox({
                  url: asset.url,
                  kind: asset.kind as 'image' | 'video',
                })
              }
            >
              {asset.kind === 'video' ? (
                <>
                  <styled.Media>
                    <video
                      src={`${asset.url}#t=0.1`}
                      muted
                      playsInline
                      preload="metadata"
                    />
                  </styled.Media>
                  <styled.PlayBadge>▶</styled.PlayBadge>
                </>
              ) : (
                <styled.Media>
                  <img
                    src={asset.url}
                    alt=""
                    loading="lazy"
                    referrerPolicy="no-referrer"
                  />
                </styled.Media>
              )}
            </styled.Thumb>
          ),
        )}
      </styled.MediaRow>
    )}
    {review.provider_response && (
      <styled.ProviderResponseBox $accent={accentColor}>
        <styled.ProviderResponseLabel $accent={accentColor}>
          Owner's response
        </styled.ProviderResponseLabel>
        <styled.ProviderResponseBody>
          {review.provider_response}
        </styled.ProviderResponseBody>
        {review.provider_responded_at ? (
          <styled.ProviderResponseMeta>
            {formatDateShort(review.provider_responded_at)}
          </styled.ProviderResponseMeta>
        ) : null}
      </styled.ProviderResponseBox>
    )}
    {review.contactable && (
      <styled.ContactRow>
        <styled.ContactLabel $accent={accentColor}>
          {review.has_responded ? 'Responds to questions ✓' : 'Open to questions'}
        </styled.ContactLabel>
        <styled.ContactButton
          type="button"
          $accent={accentColor}
          onClick={() => onAskInquiry(review)}
        >
          Ask about this experience
        </styled.ContactButton>
        <styled.ContactPrivacyNote>
          Their contact details remain private
        </styled.ContactPrivacyNote>
      </styled.ContactRow>
    )}
  </>
);
